'use client';

import { c as _c } from "react/compiler-runtime";
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { motion, useReducedMotion } from 'framer-motion';
import { BrainIcon, ChevronRightIcon } from 'lucide-react';
import type { ComponentProps, CSSProperties, ReactNode } from 'react';
import { createContext, Fragment, memo, use, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTimeout } from 'usehooks-ts';
import type { ReasoningStreamNode } from './reasoning-stream-text';
import { AI_DISCLOSURE_CONTENT_CLASS, AI_DISCLOSURE_LABEL_CLASS, AI_DISCLOSURE_TRAILING_ICON_CLASS, AI_DISCLOSURE_TRIGGER_CLASS } from './disclosure-styles';
import { planReasoningStreamNodes, REASONING_STREAM_FADE } from './reasoning-stream-text';
import { Shimmer } from './shimmer';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
type ReasoningContextValue = {
  isStreaming: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  duration: number | undefined;
  title: string | undefined;
};
const ReasoningContext = createContext<ReasoningContextValue | null>(null);
export const useReasoning = () => {
  const context = use(ReasoningContext);
  if (!context) {
    throw new Error('Reasoning components must be used within Reasoning');
  }
  return context;
};
export type ReasoningProps = ComponentProps<typeof Collapsible> & {
  isStreaming?: boolean;
  /**
   * When false, the parent owns open state. Code uses this so a live thought
   * can stay collapsed after later assistant text exists.
   */
  autoManageOpen?: boolean;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  duration?: number;
  title?: string;
};
const AUTO_CLOSE_DELAY = 1000;
const MS_IN_S = 1000;
const REASONING_CONTENT_TRANSITION = {
  duration: 0.2,
  ease: 'easeOut'
} as const;
export const Reasoning = memo(t0 => {
  const $ = _c(51);
  let children;
  let className;
  let defaultOpen;
  let durationProp;
  let onOpenChange;
  let open;
  let props;
  let t1;
  let t2;
  let title;
  if ($[0] !== t0) {
    ({
      className,
      isStreaming: t1,
      autoManageOpen: t2,
      open,
      defaultOpen,
      onOpenChange,
      duration: durationProp,
      title,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = defaultOpen;
    $[4] = durationProp;
    $[5] = onOpenChange;
    $[6] = open;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
    $[10] = title;
  } else {
    children = $[1];
    className = $[2];
    defaultOpen = $[3];
    durationProp = $[4];
    onOpenChange = $[5];
    open = $[6];
    props = $[7];
    t1 = $[8];
    t2 = $[9];
    title = $[10];
  }
  const isStreaming = t1 === undefined ? false : t1;
  const autoManageOpen = t2 === undefined ? true : t2;
  const resolvedDefaultOpen = defaultOpen ?? isStreaming;
  const isExplicitlyClosed = defaultOpen === false;
  let t3;
  if ($[11] !== onOpenChange || $[12] !== open || $[13] !== resolvedDefaultOpen) {
    t3 = {
      defaultProp: resolvedDefaultOpen,
      onChange: onOpenChange,
      prop: open
    };
    $[11] = onOpenChange;
    $[12] = open;
    $[13] = resolvedDefaultOpen;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const [isOpen, setIsOpen] = useControllableState(t3);
  let t4;
  if ($[15] !== durationProp) {
    t4 = {
      defaultProp: undefined,
      prop: durationProp
    };
    $[15] = durationProp;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const [duration, setDuration] = useControllableState(t4);
  const startTimeRef = useRef(null);
  let t5;
  if ($[17] !== isStreaming) {
    t5 = () => ({
      hasEverStreamed: isStreaming,
      hasUserInteracted: false,
      previousIsStreaming: isStreaming
    });
    $[17] = isStreaming;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const [streamInteractionState, setStreamInteractionState] = useState(t5);
  let hasEverStreamed = streamInteractionState.hasEverStreamed;
  let hasUserInteracted = streamInteractionState.hasUserInteracted;
  if (streamInteractionState.previousIsStreaming !== isStreaming) {
    const nextStreamInteractionState = {
      hasEverStreamed: streamInteractionState.hasEverStreamed || isStreaming,
      hasUserInteracted: isStreaming ? false : streamInteractionState.hasUserInteracted,
      previousIsStreaming: isStreaming
    };
    setStreamInteractionState(nextStreamInteractionState);
    hasEverStreamed = nextStreamInteractionState.hasEverStreamed;
    hasUserInteracted = nextStreamInteractionState.hasUserInteracted;
  }
  const shouldScheduleAutoClose = autoManageOpen && hasEverStreamed && !isStreaming && isOpen && !hasUserInteracted;
  let t6;
  let t7;
  if ($[19] !== isStreaming || $[20] !== setDuration) {
    t6 = () => {
      if (isStreaming) {
        if (startTimeRef.current === null) {
          startTimeRef.current = Date.now();
        }
      } else {
        if (startTimeRef.current !== null) {
          setDuration(Math.ceil((Date.now() - startTimeRef.current) / MS_IN_S));
          startTimeRef.current = null;
        }
      }
    };
    t7 = [isStreaming, setDuration];
    $[19] = isStreaming;
    $[20] = setDuration;
    $[21] = t6;
    $[22] = t7;
  } else {
    t6 = $[21];
    t7 = $[22];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[23] !== autoManageOpen || $[24] !== isExplicitlyClosed || $[25] !== isOpen || $[26] !== isStreaming || $[27] !== setIsOpen) {
    t8 = () => {
      if (!autoManageOpen) {
        return;
      }
      if (isStreaming && !isOpen && !isExplicitlyClosed) {
        setIsOpen(true);
      }
    };
    t9 = [autoManageOpen, isStreaming, isOpen, setIsOpen, isExplicitlyClosed];
    $[23] = autoManageOpen;
    $[24] = isExplicitlyClosed;
    $[25] = isOpen;
    $[26] = isStreaming;
    $[27] = setIsOpen;
    $[28] = t8;
    $[29] = t9;
  } else {
    t8 = $[28];
    t9 = $[29];
  }
  useEffect(t8, t9);
  let t10;
  if ($[30] !== setIsOpen) {
    t10 = () => {
      setIsOpen(false);
    };
    $[30] = setIsOpen;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  useTimeout(t10, shouldScheduleAutoClose ? AUTO_CLOSE_DELAY : null);
  let t11;
  if ($[32] !== setIsOpen) {
    t11 = newOpen => {
      setStreamInteractionState(_temp);
      setIsOpen(newOpen);
    };
    $[32] = setIsOpen;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  const handleOpenChange = t11;
  let t12;
  if ($[34] !== duration || $[35] !== isOpen || $[36] !== isStreaming || $[37] !== setIsOpen || $[38] !== title) {
    t12 = {
      duration,
      isOpen,
      isStreaming,
      setIsOpen,
      title
    };
    $[34] = duration;
    $[35] = isOpen;
    $[36] = isStreaming;
    $[37] = setIsOpen;
    $[38] = title;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  const contextValue = t12;
  let t13;
  if ($[40] !== className) {
    t13 = cn("group/disclosure not-prose mb-3", className);
    $[40] = className;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== children || $[43] !== handleOpenChange || $[44] !== isOpen || $[45] !== props || $[46] !== t13) {
    t14 = <Collapsible className={t13} onOpenChange={handleOpenChange} open={isOpen} {...props}>{children}</Collapsible>;
    $[42] = children;
    $[43] = handleOpenChange;
    $[44] = isOpen;
    $[45] = props;
    $[46] = t13;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  let t15;
  if ($[48] !== contextValue || $[49] !== t14) {
    t15 = <ReasoningContext value={contextValue}>{t14}</ReasoningContext>;
    $[48] = contextValue;
    $[49] = t14;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  return t15;
});
export type ReasoningTriggerProps = ComponentProps<typeof CollapsibleTrigger> & {
  getThinkingMessage?: (isStreaming: boolean, duration?: number, title?: string) => ReactNode;
};
const formatDuration = (duration: number): string => `${duration}s`;
const defaultGetThinkingMessage = (isStreaming: boolean, duration?: number, title?: string) => {
  if (isStreaming || duration === 0) {
    return <Shimmer as="span" className={AI_DISCLOSURE_LABEL_CLASS} duration={1}>
        Thinking...
      </Shimmer>;
  }
  if (title) {
    const durationSuffix = duration !== undefined ? ` · ${formatDuration(duration)}` : '';
    return <span className={cn(AI_DISCLOSURE_LABEL_CLASS, 'flex items-baseline gap-1')}>
        <span className="truncate">{title}</span>
        {durationSuffix && <span className="shrink-0 text-muted-foreground/60">{durationSuffix}</span>}
      </span>;
  }
  if (duration === undefined) {
    return <span className={AI_DISCLOSURE_LABEL_CLASS}>Thought for a few seconds</span>;
  }
  return <span className={AI_DISCLOSURE_LABEL_CLASS}>{`Thought for ${formatDuration(duration)}`}</span>;
};
export const ReasoningTrigger = memo(t0 => {
  const $ = _c(18);
  let children;
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      children,
      getThinkingMessage: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
  }
  const getThinkingMessage = t1 === undefined ? defaultGetThinkingMessage : t1;
  const {
    isStreaming,
    isOpen,
    duration,
    title
  } = useReasoning();
  let t2;
  if ($[5] !== className) {
    t2 = cn(AI_DISCLOSURE_TRIGGER_CLASS, className);
    $[5] = className;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== children || $[8] !== duration || $[9] !== getThinkingMessage || $[10] !== isOpen || $[11] !== isStreaming || $[12] !== title) {
    t3 = children ?? <><BrainIcon className="size-4 text-muted-foreground/70" />{getThinkingMessage(isStreaming, duration, title)}<ChevronRightIcon className={cn(AI_DISCLOSURE_TRAILING_ICON_CLASS, "transition-transform duration-200 ease-out motion-reduce:transition-none", isOpen ? "rotate-90" : "rotate-0")} /></>;
    $[7] = children;
    $[8] = duration;
    $[9] = getThinkingMessage;
    $[10] = isOpen;
    $[11] = isStreaming;
    $[12] = title;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== props || $[15] !== t2 || $[16] !== t3) {
    t4 = <CollapsibleTrigger className={t2} {...props}>{t3}</CollapsibleTrigger>;
    $[14] = props;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  return t4;
});
export type ReasoningContentProps = ComponentProps<typeof CollapsibleContent> & {
  children: string;
  animated?: boolean;
};
function reasoningStreamWordStyle(node: Extract<ReasoningStreamNode, {
  kind: 'word';
}>): CSSProperties {
  return {
    '--sd-animation': REASONING_STREAM_FADE.animation,
    '--sd-duration': `${node.durationMs}ms`,
    '--sd-easing': REASONING_STREAM_FADE.easing,
    ...(node.delayMs > 0 ? {
      '--sd-delay': `${Math.round(node.delayMs)}ms`
    } : {})
  } as CSSProperties;
}
function renderReasoningStreamNode(node: ReasoningStreamNode): ReactNode {
  switch (node.kind) {
    case 'text':
      return <Fragment key={node.start}>{node.value}</Fragment>;
    case 'word':
      return <span key={node.start} data-sd-animate="" style={reasoningStreamWordStyle(node)}>
          {node.value}
        </span>;
    default:
      {
        const _exhaustive: never = node;
        return _exhaustive;
      }
  }
}
function ReasoningStreamDump({
  text
}: {
  text: string;
}) {
  const committedLengthRef = useRef(0);
  const committedLength = committedLengthRef.current;
  useLayoutEffect(() => {
    committedLengthRef.current = text.length;
  }, [text]);
  return planReasoningStreamNodes(text, committedLength).map(renderReasoningStreamNode);
}
export const ReasoningContent = memo(t0 => {
  const $ = _c(27);
  let children;
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      children,
      animated: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
  }
  const animated = t1 === undefined ? true : t1;
  const {
    isOpen,
    isStreaming
  } = useReasoning();
  const shouldReduceMotion = useReducedMotion();
  const shouldAnimate = animated && !shouldReduceMotion;
  const streamWords = isStreaming && !shouldReduceMotion;
  const t2 = !isOpen;
  const t3 = !isOpen;
  let t4;
  if ($[5] !== isOpen || $[6] !== shouldAnimate) {
    t4 = !shouldAnimate ? {
      height: isOpen ? "auto" : 0,
      opacity: 1,
      y: 0
    } : {
      height: isOpen ? "auto" : 0,
      opacity: isOpen ? 1 : 0,
      y: isOpen ? 0 : -6
    };
    $[5] = isOpen;
    $[6] = shouldAnimate;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== shouldAnimate) {
    t5 = shouldAnimate ? REASONING_CONTENT_TRANSITION : {
      duration: 0
    };
    $[8] = shouldAnimate;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== className) {
    t6 = cn(AI_DISCLOSURE_CONTENT_CLASS, "overflow-hidden text-sm text-muted-foreground", className);
    $[10] = className;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== children || $[13] !== streamWords) {
    t7 = streamWords ? <ReasoningStreamDump text={children} /> : children;
    $[12] = children;
    $[13] = streamWords;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t7) {
    t8 = <pre className="whitespace-pre-wrap wrap-break-word font-mono text-xs leading-relaxed">{t7}</pre>;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t2 || $[18] !== t3 || $[19] !== t4 || $[20] !== t5 || $[21] !== t6 || $[22] !== t8) {
    t9 = <motion.div aria-hidden={t2} initial={false} inert={t3} animate={t4} transition={t5} className={t6}>{t8}</motion.div>;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== props || $[25] !== t9) {
    t10 = <CollapsibleContent forceMount={true} asChild={true} {...props}>{t9}</CollapsibleContent>;
    $[24] = props;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
});
Reasoning.displayName = 'Reasoning';
ReasoningTrigger.displayName = 'ReasoningTrigger';
ReasoningContent.displayName = 'ReasoningContent';
function _temp(current) {
  return {
    ...current,
    hasUserInteracted: true
  };
}